import {
  isRouteErrorResponse,
  useParams,
  useRouteError,
} from '@remix-run/react'
import type { ErrorResponse } from '@remix-run/router'
import { captureRemixErrorBoundaryError } from '@sentry/remix'
import { useTranslation } from 'react-i18next'

import Logo from '~/elements/logo'

import { ErrorPage } from './ErrorPage'
import { Button } from '../ui'

type StatusHandler = (info: {
  error: ErrorResponse
  params: Record<string, string | undefined>
}) => React.ReactNode | null

export function getErrorMessage(error: unknown) {
  if (typeof error === 'string') return error
  if (
    error &&
    typeof error === 'object' &&
    'message' in error &&
    typeof error.message === 'string'
  ) {
    return error.message
  }

  console.error('Unable to get error message for error', error)
  return 'Unknown Error'
}

export function GeneralErrorBoundary({
  defaultStatusHandler,
  statusHandlers,
  unexpectedErrorHandler = unexpectedErrorDefaultHandler,
  showLinkLogo = false,
}: {
  defaultStatusHandler?: StatusHandler
  statusHandlers?: Record<number, StatusHandler>
  unexpectedErrorHandler?: ({
    error,
  }: {
    error: unknown
  }) => React.ReactNode | null
  showLinkLogo?: boolean
}) {
  const { t } = useTranslation()
  const error = useRouteError()
  const params = useParams()

  captureRemixErrorBoundaryError(error)

  if (typeof document !== 'undefined') {
    console.error(error)
  }

  const fallbackStatusHandler: StatusHandler =
    defaultStatusHandler ??
    (() => (
      <ErrorPage
        title={t('errors.there_was_an_error')}
        description={t('errors.page_temporarily_unavailable')}
      >
        <Button onClick={() => window.location.reload()}>
          {t('action.try_again')}
        </Button>
        <Button variant="outline" onClick={() => window.history.back()}>
          {t('action.go_back')}
        </Button>
      </ErrorPage>
    ))

  return (
    <>
      {showLinkLogo ? (
        <div>
          <a href="/">
            <Logo />
          </a>
        </div>
      ) : null}

      {isRouteErrorResponse(error)
        ? (statusHandlers?.[error.status] ?? fallbackStatusHandler)({
            error,
            params,
          })
        : unexpectedErrorHandler({ error })}
    </>
  )
}

export const unexpectedErrorDefaultHandler = ({
  error,
}: {
  error: unknown
}) => {
  return <p>Error: {getErrorMessage(error)}</p>
}
